import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, ArrowLeft, Zap, Users, Clapperboard, Wand2, Images
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import HelpQuickStart from "./help/HelpQuickStart";
import HelpAssets from "./help/HelpAssets";
import HelpStoryboard from "./help/HelpStoryboard";
import HelpPromptEnhancement from "./help/HelpPromptEnhancement";
import HelpVisualReferences from "./help/HelpVisualReferences";

const TABS = [
  { id: "quickstart", label: "快速上手", icon: Zap, description: "一键出图出视频完整流程" },
  { id: "assets", label: "资产工坊", icon: Users, description: "角色/场景/道具管理" },
  { id: "storyboard", label: "分镜编辑器", icon: Clapperboard, description: "分镜编辑与批量操作" },
  { id: "prompt", label: "提示词增强", icon: Wand2, description: "镜头、动作、特效、情绪、构图等分镜提示词教程" },
  { id: "visual-reference", label: "视觉参考库", icon: Images, description: "后台生成的画风、焦段、景别、运镜、打光等参考图" },
];

const HelpCenter = () => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("quickstart");
  const [expandedSection, setExpandedSection] = useState(0);

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // 每个 tab 默认展开第一个 section
    const defaults = { quickstart: 0, assets: 100, storyboard: 200, prompt: 300, "visual-reference": null };
    setExpandedSection(defaults[tabId]);
  };

  return (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {/* Header */}
      <div className="border-b shrink-0" style={{ borderColor: "var(--color-border-primary)" }}>
        <div className="h-14 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="p-1.5 rounded-lg transition-all duration-200"
              style={{ color: "var(--color-text-muted)", backgroundColor: "var(--color-bg-tertiary)" }}>
              <ArrowLeft size={16} />
            </button>
            <h1 className="font-bold text-lg flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
              <BookOpen size={18} style={{ color: theme.colors.accentPrimary }} />
              使用指南
            </h1>
          </div>
        </div>

        {/* Tab 切换 */}
        <div className="flex items-center gap-1 px-6 pb-3">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium"
              style={{
                backgroundColor: activeTab === tab.id ? `${theme.colors.accentPrimary}15` : "transparent",
                color: activeTab === tab.id ? theme.colors.accentPrimary : "var(--color-text-muted)",
                border: `1px solid ${activeTab === tab.id ? `${theme.colors.accentPrimary}40` : "transparent"}`
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div key={activeTab} className={`animate-in fade-in duration-150 ${activeTab === "visual-reference" ? "max-w-6xl" : "max-w-4xl"} mx-auto p-6`}>
          {/* Tab 描述 */}
          <p className="text-sm mb-4" style={{ color: "var(--color-text-muted)" }}>
            {TABS.find(t => t.id === activeTab)?.description}
          </p>

          {activeTab === "quickstart" && (
            <HelpQuickStart theme={theme} expandedSection={expandedSection} toggleSection={toggleSection} />
          )}
          {activeTab === "assets" && (
            <HelpAssets theme={theme} expandedSection={expandedSection} toggleSection={toggleSection} />
          )}
          {activeTab === "storyboard" && (
            <HelpStoryboard theme={theme} expandedSection={expandedSection} toggleSection={toggleSection} />
          )}
          {activeTab === "prompt" && (
            <HelpPromptEnhancement theme={theme} expandedSection={expandedSection} toggleSection={toggleSection} />
          )}
          {activeTab === "visual-reference" && (
            <HelpVisualReferences theme={theme} />
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
